import useBulkActions from "@entities/brief/lib/hooks/useBulkActions";
import { useGetBriefs } from "@entities/brief/lib/hooks/useGetBriefs";
import { useSuggestInfluencerPricing } from "@entities/brief/lib/hooks/useSuggestInfluencerPricing";
import { BriefNotificationActionType, CopyType } from "@shared/lib/types/brief";
import { IBriefInfluencerSearchRequestV2 } from "@shared/lib/types/briefInfluencerV2";
import { Button, Modal } from "@shared/ui";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { Close } from "@shared/ui/icons";
import { SelectButton } from "@shared/ui/Select";
import type { RadioChangeEvent } from "antd";
import { Radio } from "antd";
import { Share2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import styles from "./styles.module.scss";

interface IAddToCampaignFromBriefProps {
  briefId: string;
  onClose: () => void;
  open: boolean;
  selectedBriefInfluencers: string[];
  selectAll?: boolean;
  checkedList?: string[];
  uncheckList?: string[];
  totalCount?: number;
  searchPayload: Partial<IBriefInfluencerSearchRequestV2>;
  isInAll?: boolean;
}

export function AddToCampaignFromBrief({
  briefId,
  onClose,
  open,
  selectedBriefInfluencers,
  selectAll,
  checkedList,
  uncheckList,
  totalCount,
  searchPayload,
  isInAll,
}: IAddToCampaignFromBriefProps) {
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    null
  );

  // const { userDetails } = useGetMe();
  const isAddToCampaignDisabled = false;
  // userDetails?.userEntity?.permissions.includes("super-brand-org") ||
  // userDetails?.userEntity?.permissions.includes("super")
  //   ? false
  //   : !userDetails?.userEntity?.permissions.includes("modify-briefs-org");
  const [transferOption, setTransferOption] = useState<CopyType>(
    CopyType.COPY_ONLY
  );
  const [searchQuery, setSearchQuery] = useState("");

  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  const { briefs, isFetching: isFetchingBriefs } = useGetBriefs({
    pageNo: 1,
    limit: 50,
    searchQuery: debouncedSearchQuery,
  });

  const { suggestWithPricing, isSuggesting } = useSuggestInfluencerPricing(
    () => {
      onClose();
      handleClose();
    }
  );

  const { bulkActions, isBulkActions, isBulkActionsSuccess, bulkActionsError } =
    useBulkActions();

  useEffect(() => {
    if (isBulkActionsSuccess) {
      handleClose();
    }
  }, [isBulkActionsSuccess]);

  const campaignOptions = useMemo(() => {
    return briefs.map((brief) => ({
      label: brief.name,
      value: brief._id,
    }));
  }, [briefs]);

  const selectedCampaign = useMemo(() => {
    return briefs.find((brief) => brief._id === selectedCampaignId) || null;
  }, [briefs, selectedCampaignId]);

  const handleAddToCampaign = () => {
    if (!selectedCampaign) return;
    // bulkActions({
    //     briefId: selectedCampaign._id,

    //     // copyType: transferOption,
    //     // selectAll,
    //     // uncheckList,
    // });
    if (isInAll) {
      suggestWithPricing({
        data: {
          briefId: selectedCampaign._id,
          briefInfluencerIds: selectedBriefInfluencers,
          copyType: transferOption,
        },
      });
    } else {
      bulkActions({
        briefId,
        actionType: BriefNotificationActionType.copyToAnotherBrief,
        ids: checkedList || [],
        notIncludeIds: uncheckList,
        isSelectAll: selectAll,
        filterParams: { ...searchPayload },
        actionParams: {
          copyType: transferOption,
          copyToBriefId: selectedCampaignId!,
        },
      });
    }
  };

  const handleClose = () => {
    setSelectedCampaignId(null);
    setTransferOption(CopyType.COPY_ONLY);
    setSearchQuery("");
    onClose();
  };

  const count =
    selectAll && totalCount
      ? totalCount - (uncheckList?.length ?? 0)
      : selectedBriefInfluencers.length;

  const submitButton = (
    <DisabledPopover isDisabled={isAddToCampaignDisabled}>
      <Button
        htmlType="submit"
        type="primary"
        loading={isBulkActions || isSuggesting}
        disabled={
          !selectedCampaignId ||
          isBulkActions ||
          isSuggesting ||
          isAddToCampaignDisabled
        }
        className={styles.submitButton}
        icon={<Share2 size={16} />}
      >
        Add {count} Creators
      </Button>
    </DisabledPopover>
  );

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={500}
      className={styles.modal}
      closeIcon={<Close size={20} />}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Add to Another Campaign</h3>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAddToCampaign();
          }}
          className={styles.form}
        >
          <div className={styles.formSection}>
            <label className={styles.label}>Destination Campaign</label>
            <div className={styles.selectWrapper}>
              <SelectButton
                value={selectedCampaignId || undefined}
                isLoading={isFetchingBriefs}
                onChange={(value) => setSelectedCampaignId(value as string)}
                options={campaignOptions}
                placeholder="Select campaign..."
                searchable
                searchValue={searchQuery}
                onSearchChange={setSearchQuery}
                className={styles.selectButton}
                size="large"
                label="Select campaign..."
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <label className={styles.label}>Transfer Options</label>
            <Radio.Group
              value={transferOption}
              onChange={(e: RadioChangeEvent) =>
                setTransferOption(e.target.value as CopyType)
              }
              className={styles.radioGroup}
            >
              <label
                className={`${styles.radioOption} ${
                  transferOption === CopyType.COPY_ONLY
                    ? styles.radioOptionSelected
                    : ""
                }`}
              >
                <Radio value={CopyType.COPY_ONLY} className={styles.radio} />
                <div className={styles.radioContent}>
                  <div className={styles.radioTitle}>Add creators</div>
                  <div className={styles.radioDescription}>
                    Only adds the creator profiles. Deliverables and costs are
                    reset.
                  </div>
                </div>
              </label>

              <label
                className={`${styles.radioOption} ${
                  transferOption === CopyType.COPY_WITH_DELIVERABLES
                    ? styles.radioOptionSelected
                    : ""
                }`}
              >
                <Radio
                  value={CopyType.COPY_WITH_DELIVERABLES}
                  className={styles.radio}
                />
                <div className={styles.radioContent}>
                  <div className={styles.radioTitle}>Add with deliverables</div>
                  <div className={styles.radioDescription}>
                    Copies the current package and deliverables structure.
                  </div>
                </div>
              </label>

              <label
                className={`${styles.radioOption} ${
                  transferOption === CopyType.COPY_WITH_DELIVERABLES_AND_PRICE
                    ? styles.radioOptionSelected
                    : ""
                }`}
              >
                <Radio
                  value={CopyType.COPY_WITH_DELIVERABLES_AND_PRICE}
                  className={styles.radio}
                />
                <div className={styles.radioContent}>
                  <div className={styles.radioTitle}>
                    Add with deliverables & set target price
                  </div>
                  <div className={styles.radioDescription}>
                    Copies deliverables and sets current cost as the target
                    price.
                  </div>
                </div>
              </label>
            </Radio.Group>
          </div>

          <div className={styles.formActions}>
            <Button onClick={handleClose} className={styles.cancelButton}>
              Cancel
            </Button>
            {submitButton}
          </div>
        </form>
      </div>
    </Modal>
  );
}
